import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

const STORAGE_KEY = "starsema-language";

const translations = {
  en: {
    navigation: {
      Home: "Home",
      About: "About",
      Events: "Services & Events",
      Sermons: "Sermons",
      Donations: "Donation",
      Calendar: "Calendar",
      Contact: "Contact",
      MemberPortal: "Member Portal",
      Login: "Login",
    },
    layout: {
      churchInMadison: "Ethiopian Orthodox Tewahido Church in Madison, Wisconsin",
      mission:
        "We gather in prayer, sacramental life, teaching, and service while preserving the sacred tradition of the Ethiopian Orthodox Tewahido Church.",
      currentServiceLabel: "Current Service",
      memberPortal: "Member Portal",
      give: "Give",
      explore: "Explore",
      serviceRhythm: "Service Rhythm",
      contactUs: "Contact Us",
      builtFor:
        "Built to support parish communication, worship information, and community stewardship.",
      allRightsReserved: "All rights reserved.",
      poweredBy: "Powered by",
      scripture:
        '"For where two or three gather in my name, there am I with them." Matthew 18:20',
      languageLabel: "Language",
      english: "English",
      amharic: "አማርኛ",
      meetingLocation:
        "Assumption Greek Orthodox Church, lower level gathering space",
    },
    notices: {
      title: "Language support update",
      body:
        "This page remains in English for now. Full Amharic support is planned in a future update.",
    },
    home: {
      heroBadge: "St. Arsema Ethiopian Orthodox Tewahido Church",
      heroTitle:
        "In the name of the Father, and of the Son, and of the Holy Spirit. One God Amen.",
      heroBody:
        "We gather in prayer, sacramental life, teaching, and service while preserving the sacred tradition of the Ethiopian Orthodox Tewahido Church.",
      heroPrimary: "Plan Your Visit",
      heroSecondary: "Learn About the Parish",
      visitThisWeek: "Visit This Week",
      time: "Time",
      location: "Location",
      welcomeEyebrow: "Welcome to St. Arsema",
      welcomeTitle: "Ethiopian Orthodox worship, prayer, and community in Madison.",
      welcomeBody1:
        "St. Arsema carries the spiritual inheritance of the Ethiopian Orthodox Tewahedo Church through prayer, sacred chant, fasting, teaching, fellowship, and reverence for Holy Tradition. Families, longtime faithful, visitors, and those exploring Orthodox Christianity are all welcome.",
      welcomeBody2:
        "The parish is currently gathering in the lower-level space of Assumption Greek Orthodox Church while praying and working toward a permanent home of its own in Madison.",
      welcomeAppealTitle: "Help us build a lasting home for the parish.",
      welcomeAppealBody:
        "Your support helps the parish move from a shared meeting space into a dedicated church home for worship, sacraments, youth formation, teaching, and long-term stability.",
      welcomePrimary: "Support the Church Home Fund",
      welcomeSecondary: "Visit the Parish",
      cards: [
        {
          title: "Worship",
          text: "A prayerful community formed by Scripture, fasting, holy tradition, and sacramental life.",
        },
        {
          title: "A Permanent Church Home",
          text: "We are gathering in a shared lower-level space today while preparing, praying, and giving toward a permanent church home in the future.",
        },
        {
          title: "Faith for the Next Generation",
          text: "The parish is investing in worship, teaching, language, and formation so children and families can inherit the Ethiopian Orthodox faith with clarity and joy.",
        },
      ],
      startHere: "Start Here",
      quickLinksTitle: "Clear paths for visitors, families, and members",
      quickLinksBody:
        "The website should help people quickly find worship times, parish teaching, location details, and next steps for getting connected.",
      openPage: "Open page",
      parishLife: "Parish Life",
      parishLifeBody:
        "A quick look at the current rhythm of worship, teaching, and community life.",
      viewAll: "View all",
      stewardshipBadge: "Stewardship and community care",
      stewardshipTitle: "Help strengthen worship, teaching, and the future of the parish.",
      stewardshipBody:
        "Giving supports parish life today and helps St. Arsema work toward a permanent church home in Madison.",
      stewardshipPrimary: "Give Online",
      stewardshipSecondary: "Ask a Stewardship Question",
      quickLinks: [
        {
          title: "Plan Your Visit",
          description: "Directions, service rhythm, and what to expect when you arrive.",
          page: "Contact",
        },
        {
          title: "Learn Our Story",
          description:
            "Read about the parish mission and the heritage of the Ethiopian Orthodox Church.",
          page: "About",
        },
        {
          title: "See Upcoming Gatherings",
          description: "Stay current with worship times, parish events, and fellowship.",
          page: "Events",
        },
        {
          title: "Support the Parish",
          description:
            "Give securely toward worship, ministry, outreach, and a future church home.",
          page: "Donations",
        },
      ],
      serviceSchedule: [
        {
          title: "Every Other Saturday Sermon and Prayer",
          time: "4:00 PM to 7:00 PM",
          detail:
            "The parish gathers for worship, fellowship, and teaching every other Saturday in Madison.",
        },
        {
          title: "Children and Youth Formation",
          time: "During parish gatherings",
          detail:
            "Young people receive age-appropriate Orthodox instruction and community support.",
        },
        {
          title: "Feast Day and Special Services",
          time: "As announced",
          detail:
            "Additional liturgies, fasts, and feast celebrations are shared through parish communication channels.",
        },
      ],
      events: [
        {
          title: "Parish Sermon and Prayer Gathering",
          category: "Worship",
          dateLabel: "Every other Saturday",
          time: "4:00 PM to 7:00 PM",
          location: "11 N 7th Street, Madison, WI 53704",
          description:
            "A regular parish gathering for worship, prayer, teaching, and fellowship.",
          featured: true,
        },
        {
          title: "Youth Fellowship and Teaching",
          category: "Youth",
          dateLabel: "Every other Saturday",
          time: "4:00 PM to 7:00 PM",
          location: "11 N 7th Street, Madison, WI 53704",
          description:
            "Young people gather for faith formation, discussion, and community rooted in Orthodox life.",
          featured: false,
        },
        {
          title: "Parish Building and Stewardship Updates",
          category: "Community",
          dateLabel: "Scheduled as needed",
          time: "After the main gathering",
          location: "On site and through parish communications",
          description:
            "Members receive ministry updates, stewardship news, and next steps as the parish grows.",
          featured: false,
        },
      ],
    },
    about: {
      eyebrow: "About Our Church",
      title: "St. Arsema Ethiopian Orthodox Tewahido Church",
      intro:
        "St. Arsema Ethiopian Orthodox Tewahido Church in Madison, Wisconsin, is a parish of the Ethiopian Orthodox Tewahedo faith tradition. This page offers church-approved placeholder content that can later be refined by parish leadership.",
      whoWeAreTitle: "Who We Are",
      whoWeAreParagraphs: [
        "The Ethiopian Orthodox Tewahedo Church is one of the world’s oldest Christian traditions. It preserves a liturgical, sacramental, scriptural, and communal life shaped over many centuries.",
        "St. Arsema Church exists to gather Orthodox families and visitors in Madison for prayer, worship, teaching, service, and patient parish building. This content is intentionally meaningful but can be updated later by the church.",
      ],
      sideTitle: "What this page should communicate",
      sideCards: [
        {
          title: "Orthodox identity",
          description: "The parish belongs to the historic Ethiopian Orthodox Tewahedo Church and should present that tradition with clarity and reverence.",
        },
        {
          title: "Local presence",
          description: "The church serves real families, students, children, and newcomers in Madison, Wisconsin.",
        },
        {
          title: "Future growth",
          description: "The site should communicate both present worship life and long-term plans for a permanent church home.",
        },
      ],
      historyTitle: "History and Tradition",
      historyCards: [
        {
          title: "Ancient roots",
          description: "The Ethiopian Orthodox Tewahedo Church stands within a very old Christian inheritance shaped by Scripture, liturgy, fasting, sacred music, and communal prayer.",
        },
        {
          title: "Biblical witness",
          description: "Christian memory of Ethiopia reaches back to the earliest centuries of the Church, and Ethiopian Christianity continues to treasure that biblical and apostolic inheritance.",
        },
        {
          title: "Living worship",
          description: "Orthodox life is preserved not only in books but also in prayer, feasts, chant, discipline, service, and faithful parish community.",
        },
        {
          title: "Diaspora continuity",
          description: "Parishes like St. Arsema help families preserve Orthodox faith and Ethiopian Christian culture in new communities.",
        },
      ],
      structureTitle: "Church Organization",
      structureIntro:
        "Church life is not independent or self-invented. The parish lives under ecclesiastical authority, tradition, and accountability.",
      structureItems: [
        {
          title: "Holy Synod",
          description: "The Holy Synod safeguards faith, teaching, worship, and order in the wider Ethiopian Orthodox Tewahedo Church.",
        },
        {
          title: "Diocesan oversight",
          description: "Bishops and diocesan leadership guide parishes pastorally and administratively.",
        },
        {
          title: "Parish leadership",
          description: "Local clergy and lay leaders serve together for worship, teaching, stewardship, and ministry coordination.",
        },
        {
          title: "Faithful households",
          description: "The parish is sustained by families, youth, elders, and members who gather in prayer and service.",
        },
      ],
      purposeTitle: "Purpose of the Parish",
      purposeBody:
        "St. Arsema Church exists to worship God, build up the faithful, teach the next generation, welcome households into Orthodox life, and prepare responsibly for long-term parish stability.",
      purposePoints: [
        "Provide spiritual services and sacramental life to the faithful.",
        "Offer teaching, guidance, and formation for children, youth, and adults.",
        "Strengthen unity, hospitality, and practical care within the parish.",
        "Prepare the community for a permanent church home in Madison.",
      ],
      closingTitle: "Our Commitment",
      closingBody:
        "This page is complete enough for production use today and can be refined later with clergy-approved language, historical notes, photos, and official diocesan references.",
    },
    events: {
      title: "Services and Events",
      intro:
        "A clear overview of worship times, parish gatherings, and the normal rhythm of church life.",
      rhythmTitle: "Regular parish rhythm",
      featured: "Featured",
      timeLabel: "Time",
      locationLabel: "Location",
    },
    sermons: {
      title: "Sermons",
      intro:
        "Messages, reflections, and teaching summaries for the parish. Media links can be added later when official recordings are available.",
      archiveTitle: "Sermon archive direction",
      archiveBody:
        "This production version uses meaningful placeholder summaries instead of fake playback controls. The church can later replace these with approved sermon titles, speakers, and media links.",
      by: "By",
      scripture: "Scripture",
      audioAvailable: "Audio available",
      audioSoon: "Audio coming soon",
      videoAvailable: "Video available",
      videoSoon: "Video archive in progress",
      sermons: [
        {
          title: "Holding Fast to the Faith",
          speaker: "Mr. Eshetu Zatu",
          dateLabel: "Recent parish teaching",
          scripture: "Hebrews 11",
          summary:
            "An encouragement to remain faithful, prayerful, and steadfast in the life of the Church.",
        },
        {
          title: "Prayer, Patience, and Orthodox Witness",
          speaker: "Parish teaching team",
          dateLabel: "Parish archive item",
          scripture: "Romans 12",
          summary:
            "A reflection on how worship shapes daily life, family formation, and service to the community.",
        },
      ],
    },
    contact: {
      title: "Contact Us",
      intro:
        "Reach out with questions, prayer requests, visit planning, or parish inquiries.",
      formTitle: "Send a Message",
      formIntro:
        "This contact form opens your email client with a message pre-filled to the parish mailbox.",
      name: "Name",
      namePlaceholder: "Your full name",
      email: "Email",
      emailPlaceholder: "you@example.com",
      phone: "Phone (optional)",
      phonePlaceholder: "(123) 456-7890",
      subject: "Subject",
      subjectPlaceholder: "How can we help?",
      message: "Message",
      messagePlaceholder: "Write your message here",
      send: "Send message",
      missingFields: "Please fill in your name, email, and message.",
      emailClientOpened:
        "Your email client should open with a pre-filled message to the parish.",
      visitTitle: "Visit or call us",
      planningTitle: "Planning a visit?",
      planningBody:
        "Visitors are welcome. If this will be your first time, send a note and the parish can help with directions, timing, and what to expect.",
      directions: "Open directions in Google Maps",
      mapTitle: "Map to 11 N 7th Street, Madison, WI 53704",
    },
  },
  am: {
    navigation: {
      Home: "መነሻ",
      About: "ስለ ቤተክርስቲያኑ",
      Events: "አገልግሎቶችና ዝግጅቶች",
      Sermons: "ስብከቶች",
      Donations: "ስጦታ",
      Calendar: "ቀን መቁጠሪያ",
      Contact: "ያግኙን",
      MemberPortal: "የአባላት መድረክ",
      Login: "መግቢያ",
    },
    layout: {
      churchInMadison: "የኢትዮጵያ ኦርቶዶክስ ተዋሕዶ ቤተክርስቲያን በማዲሰን ዊስኮንሲን",
      mission:
        "በጸሎት፣ በምሥጢራዊ ሕይወት፣ በትምህርት እና በአገልግሎት እንሰበሰባለን፣ እንዲሁም የኢትዮጵያ ኦርቶዶክስ ተዋሕዶ ቤተክርስቲያንን ቅዱስ ትውፊት እንጠብቃለን።",
      currentServiceLabel: "የአሁኑ አገልግሎት",
      memberPortal: "የአባላት መድረክ",
      give: "ስጦታ",
      explore: "ክፍሎች",
      serviceRhythm: "የአገልግሎት ስርዓት",
      contactUs: "ያግኙን",
      builtFor: "ይህ ድረ-ገጽ ለወንጌላዊ ግንኙነት፣ ለአምልኮ መረጃ እና ለማህበረሰብ አስተዳደር ተዘጋጅቷል።",
      allRightsReserved: "መብቱ በሙሉ የተጠበቀ ነው።",
      poweredBy: "የተሰራው በ",
      scripture: '"በሁለት ወይም በሦስት በስሜ የተሰበሰቡ በዚያ መካከላቸው እኔ አለሁ።" ማቴዎስ 18:20',
      languageLabel: "ቋንቋ",
      english: "English",
      amharic: "አማርኛ",
      meetingLocation:
        "በAssumption Greek Orthodox Church የታችኛው መሰብሰቢያ ቦታ",
    },
    notices: {
      title: "የቋንቋ ድጋፍ ማስታወቂያ",
      body: "ይህ ገጽ አሁን ለጊዜው በእንግሊዝኛ ብቻ ይታያል። ሙሉ የአማርኛ ድጋፍ በሚቀጥለው ዝማኔ ይመጣል።",
    },
    home: {
      heroBadge: "ቅድስት አርሴማ የኢትዮጵያ ኦርቶዶክስ ተዋሕዶ ቤተክርስቲያን",
      heroTitle: "በአብ ስም፣ በወልድ ስም፣ በመንፈስ ቅዱስም ስም። አንድ አምላክ አሜን።",
      heroBody:
        "በጸሎት፣ በምሥጢራዊ ሕይወት፣ በትምህርት እና በአገልግሎት እንሰበሰባለን፣ እንዲሁም የኢትዮጵያ ኦርቶዶክስ ተዋሕዶ ቤተክርስቲያንን ቅዱስ ትውፊት እንጠብቃለን።",
      heroPrimary: "ጉብኝትዎን ያቅዱ",
      heroSecondary: "ስለ ማህበሩ ይወቁ",
      visitThisWeek: "በዚህ ሳምንት ይጎብኙ",
      time: "ሰዓት",
      location: "ቦታ",
      welcomeEyebrow: "ወደ ቅድስት አርሴማ እንኳን ደህና መጡ",
      welcomeTitle: "የኢትዮጵያ ኦርቶዶክስ አምልኮ፣ ጸሎት እና ማህበረሰብ በማዲሰን።",
      welcomeBody1:
        "ቅድስት አርሴማ በጸሎት፣ በቅዱስ ዝማሬ፣ በጾም፣ በትምህርት፣ በህብረት እና በቅዱስ ትውፊት ክብር የኢትዮጵያ ኦርቶዶክስ ተዋሕዶ ቤተክርስቲያንን መንፈሳዊ ቅርስ ትሸከማለች። ቤተሰቦች፣ እንግዶች እና የኦርቶዶክስን ክርስትና ለማወቅ የሚፈልጉ ሁሉ እንኳን ደህና መጡ።",
      welcomeBody2:
        "ማህበሩ አሁን በAssumption Greek Orthodox Church የታችኛው መሰብሰቢያ ቦታ ይገናኛል፣ በወደፊትም በማዲሰን የራሱን ቋሚ ቤተክርስቲያን ለማግኘት በጸሎትና በጥረት ላይ ነው።",
      welcomeAppealTitle: "ለማህበሩ የሚቆይ ቤት እንገንባ።",
      welcomeAppealBody:
        "ድጋፍዎ ማህበሩን ከተጋራ መሰብሰቢያ ቦታ ወደ ራሱ የሆነ ለአምልኮ፣ ለቅዱሳን ምሥጢራት፣ ለወጣቶች ስልጠና እና ለረጅም ጊዜ መረጋጋት የተሰጠ ቤተክርስቲያን ለመግባት ይረዳል።",
      welcomePrimary: "የቤተክርስቲያን ቤት ፈንድን ይደግፉ",
      welcomeSecondary: "ማህበሩን ይጎብኙ",
      cards: [
        {
          title: "አምልኮ",
          text: "በመጽሐፍ ቅዱስ፣ በጾም፣ በቅዱስ ትውፊት እና በምሥጢራዊ ሕይወት የተቀረጸ ጸሎታዊ ማህበር።",
        },
        {
          title: "ቋሚ የቤተክርስቲያን ቤት",
          text: "ዛሬ በተጋራ የታችኛው መሰብሰቢያ ቦታ እንገናኛለን፤ ለወደፊት ግን ቋሚ የቤተክርስቲያን ቤት ለማግኘት በጸሎትና በመስጠት እንሰራለን።",
        },
        {
          title: "ለሚቀጥለው ትውልድ እምነት",
          text: "ልጆችና ቤተሰቦች የኢትዮጵያ ኦርቶዶክስ እምነትን በግልጽነትና በደስታ እንዲወርሱ ማህበሩ በአምልኮ፣ በትምህርት እና በቋንቋ ስልጠና ላይ ይሠራል።",
        },
      ],
      startHere: "ከዚህ ይጀምሩ",
      quickLinksTitle: "ለእንግዶች፣ ለቤተሰቦች እና ለአባላት ግልጽ መንገዶች",
      quickLinksBody:
        "ድረ-ገጹ ሰዎች የአምልኮ ጊዜ፣ የማህበሩን ትምህርት፣ የቦታ መረጃ እና የሚቀጥሉ እርምጃዎችን በፍጥነት እንዲያገኙ ሊረዳ ይገባል።",
      openPage: "ገጹን ይክፈቱ",
      parishLife: "የማህበሩ ሕይወት",
      parishLifeBody: "የአምልኮ፣ የትምህርት እና የማህበረሰብ ሕይወት አጭር እይታ።",
      viewAll: "ሁሉንም ይመልከቱ",
      stewardshipBadge: "አስተዳደር እና ማህበረሰብ እንክብካቤ",
      stewardshipTitle: "አምልኮን፣ ትምህርትን እና የማህበሩን ወደፊት ይጠናክሩ።",
      stewardshipBody:
        "ስጦታ የዛሬን የማህበሩን ሕይወት ይደግፋል እና ቅድስት አርሴማ በማዲሰን ቋሚ የቤተክርስቲያን ቤት እንድታገኝ ያግዛል።",
      stewardshipPrimary: "በመስመር ላይ ይስጡ",
      stewardshipSecondary: "የስጦታ ጥያቄ ይጠይቁ",
      quickLinks: [
        {
          title: "ጉብኝትዎን ያቅዱ",
          description: "አቅጣጫዎች፣ የአገልግሎት ስርዓት እና ሲመጡ የሚጠበቁት።",
          page: "Contact",
        },
        {
          title: "ታሪካችንን ይወቁ",
          description: "ስለ ማህበሩ ተልዕኮ እና ስለ የኢትዮጵያ ኦርቶዶክስ ቤተክርስቲያን ቅርስ ያንብቡ።",
          page: "About",
        },
        {
          title: "የሚመጡ መሰብሰቦችን ይመልከቱ",
          description: "የአምልኮ ጊዜዎችን፣ የማህበሩን ዝግጅቶች እና የህብረት ጊዜዎችን ይከታተሉ።",
          page: "Events",
        },
        {
          title: "ማህበሩን ይደግፉ",
          description: "ለአምልኮ፣ ለአገልግሎት፣ ለስብከት እና ለወደፊት የቤተክርስቲያን ቤት በደህና ይስጡ።",
          page: "Donations",
        },
      ],
      serviceSchedule: [
        {
          title: "በየሁለት ሳምንቱ ስብከት እና ጸሎት",
          time: "4:00 ከሰዓት እስከ 7:00 ከሰዓት",
          detail: "ማህበሩ በማዲሰን በየሁለት ሳምንቱ ለአምልኮ፣ ለህብረት እና ለትምህርት ይሰበሰባል።",
        },
        {
          title: "የልጆችና የወጣቶች ስልጠና",
          time: "በማህበሩ መሰብሰቦች ጊዜ",
          detail: "ወጣቶች እድሜያቸውን የሚመጥን የኦርቶዶክስ ትምህርት እና የማህበረሰብ ድጋፍ ያገኛሉ።",
        },
        {
          title: "የበዓል ቀናት እና ልዩ አገልግሎቶች",
          time: "እንደሚገለጽ",
          detail: "ተጨማሪ ቅዳሴዎች፣ ጾሞች እና በዓላት በማህበሩ የግንኙነት መንገዶች ይጋራሉ።",
        },
      ],
      events: [
        {
          title: "የማህበሩ ስብከት እና ጸሎት ስብሰባ",
          category: "አምልኮ",
          dateLabel: "በየሁለት ሳምንቱ",
          time: "4:00 ከሰዓት እስከ 7:00 ከሰዓት",
          location: "11 N 7th Street, Madison, WI 53704",
          description: "መደበኛ የአምልኮ፣ የጸሎት፣ የትምህርት እና የህብረት ስብሰባ።",
          featured: true,
        },
        {
          title: "የወጣቶች ህብረት እና ትምህርት",
          category: "ወጣቶች",
          dateLabel: "በየሁለት ሳምንቱ",
          time: "4:00 ከሰዓት እስከ 7:00 ከሰዓት",
          location: "11 N 7th Street, Madison, WI 53704",
          description: "ወጣቶች ለእምነት ስልጠና፣ ለውይይት እና ለኦርቶዶክስ ሕይወት የተመሰረተ ህብረት ይሰበሰባሉ።",
          featured: false,
        },
        {
          title: "የማህበሩ ሕንፃ እና አስተዳደር ዝማኔዎች",
          category: "ማህበረሰብ",
          dateLabel: "እንደ አስፈላጊነቱ",
          time: "ከዋናው ስብሰባ በኋላ",
          location: "በስፍራው እና በማህበሩ ግንኙነት መንገዶች",
          description: "አባላት የአገልግሎት ዝማኔዎችን፣ የአስተዳደር ዜናዎችን እና ቀጣይ እርምጃዎችን ያገኛሉ።",
          featured: false,
        },
      ],
    },
    about: {
      eyebrow: "ስለ ቤተክርስቲያናችን",
      title: "ቅድስት አርሴማ የኢትዮጵያ ኦርቶዶክስ ተዋሕዶ ቤተክርስቲያን",
      intro:
        "ቅድስት አርሴማ በማዲሰን፣ ዊስኮንሲን የሚገኝ የኢትዮጵያ ኦርቶዶክስ ተዋሕዶ ማህበር ነው። ይህ ገጽ በኋላ በማህበሩ ሊቀየር የሚችል ትርጉም ያለው የመጀመሪያ ይዘት ያቀርባል።",
      whoWeAreTitle: "እኛ ማን ነን",
      whoWeAreParagraphs: [
        "የኢትዮጵያ ኦርቶዶክስ ተዋሕዶ ቤተክርስቲያን ከዓለም አሮጌ ክርስቲያናዊ ትውፊቶች አንዱ ናት። ሊቱርጂያ፣ ምሥጢራዊ ሕይወት፣ መጽሐፍ ቅዱሳዊ ትምህርት እና ማህበረሰብ ሕይወትን በመሸከም ብዙ ሺህ ዓመታት ቆይታለች።",
        "ቅድስት አርሴማ በማዲሰን ውስጥ ኦርቶዶክስ ቤተሰቦችንና እንግዶችን ለጸሎት፣ ለአምልኮ፣ ለትምህርት፣ ለአገልግሎት እና ለማህበረሰብ ለመሰብሰብ ትኖራለች።",
      ],
      sideTitle: "ይህ ገጽ ምን ሊያሳይ ይገባል",
      sideCards: [
        {
          title: "ኦርቶዶክስ መለያ",
          description: "ማህበሩ የታሪካዊቷ የኢትዮጵያ ኦርቶዶክስ ተዋሕዶ ቤተክርስቲያን አባል መሆኑን በግልጽነትና በክብር ማቅረብ አለበት።",
        },
        {
          title: "አካባቢያዊ መገኘት",
          description: "ቤተክርስቲያኑ በማዲሰን ውስጥ ያሉ ቤተሰቦችን፣ ተማሪዎችን፣ ልጆችን እና አዲስ መጪዎችን ያገለግላል።",
        },
        {
          title: "ወደፊት እድገት",
          description: "ድረ-ገጹ የአሁኑን የአምልኮ ሕይወት እና ቋሚ የቤተክርስቲያን ቤት የማግኘት የወደፊት እቅድ ሊገልጽ ይገባል።",
        },
      ],
      historyTitle: "ታሪክ እና ትውፊት",
      historyCards: [
        {
          title: "ጥንታዊ ሥሮች",
          description: "የኢትዮጵያ ኦርቶዶክስ ተዋሕዶ ቤተክርስቲያን በመጽሐፍ ቅዱስ፣ በሊቱርጂያ፣ በጾም፣ በቅዱስ ዝማሬ እና በህብረታዊ ጸሎት የተሠራች አሮጌ የክርስቲያን ትውፊት ናት።",
        },
        {
          title: "መጽሐፍ ቅዱሳዊ ምስክርነት",
          description: "የኢትዮጵያ ክርስትና ከመጀመሪያዎቹ ዘመናት ጀምሮ ያለውን መጽሐፍ ቅዱሳዊና ሐዋርያዊ ቅርስ በክብር ትጠብቃለች።",
        },
        {
          title: "ሕያው አምልኮ",
          description: "ኦርቶዶክስ ሕይወት በመጻሕፍት ብቻ አይቀመጥም፤ በጸሎት፣ በበዓላት፣ በዝማሬ፣ በቅድስና ስርዓት እና በታማኝ ማህበረሰብ ሕይወት ይቀጥላል።",
        },
        {
          title: "በውጭ አገር ቀጣይነት",
          description: "እንደ ቅድስት አርሴማ ያሉ ማህበሮች ቤተሰቦች የኦርቶዶክስ እምነትን እና የኢትዮጵያን ክርስቲያናዊ ባህል እንዲጠብቁ ይረዳሉ።",
        },
      ],
      structureTitle: "የቤተክርስቲያን አደረጃጀት",
      structureIntro:
        "የቤተክርስቲያን ሕይወት እንደፈለገ የሚፈጠር አይደለም። ማህበሩ በትውፊት፣ በሥርዓት እና በተጠያቂነት ይኖራል።",
      structureItems: [
        {
          title: "ቅዱስ ሲኖዶስ",
          description: "ቅዱስ ሲኖዶስ የሰፊውን የኢትዮጵያ ኦርቶዶክስ ተዋሕዶ ቤተክርስቲያን እምነት፣ ትምህርት፣ አምልኮ እና ሥርዓት ይጠብቃል።",
        },
        {
          title: "የሀገረ ስብከት እንክብካቤ",
          description: "ካህናትና የሀገረ ስብከት መሪዎች ማህበሮችን በመንፈሳዊና በአስተዳደራዊ ሁኔታ ይመራሉ።",
        },
        {
          title: "የማህበር መሪነት",
          description: "ካህናትና ሕዝባዊ መሪዎች በአምልኮ፣ በትምህርት፣ በአስተዳደር እና በአገልግሎት በአንድነት ያገለግላሉ።",
        },
        {
          title: "ታማኝ ቤተሰቦች",
          description: "ማህበሩ በቤተሰቦች፣ በወጣቶች፣ በአረጋውያን እና በጸሎትና በአገልግሎት የሚገናኙ አባላት ይጸናል።",
        },
      ],
      purposeTitle: "የማህበሩ ዓላማ",
      purposeBody:
        "ቅድስት አርሴማ ቤተክርስቲያን እግዚአብሔርን ለማምለክ፣ ምዕመናንን ለማበረታታት፣ ትውልዱን ለማስተማር፣ ቤተሰቦችን ወደ ኦርቶዶክስ ሕይወት ለማስገባት እና ለወደፊት የማህበሩ መረጋጋት በኃላፊነት ለመዘጋጀት ትኖራለች።",
      purposePoints: [
        "ለምዕመናን መንፈሳዊ አገልግሎትና ምሥጢራዊ ሕይወት መስጠት።",
        "ለልጆች፣ ለወጣቶች እና ለወጣት እድሜዎች ትምህርት፣ መመሪያ እና ስልጠና መስጠት።",
        "በማህበሩ ውስጥ አንድነት፣ እንግዳነት እና እንክብካቤን ማጠናከር።",
        "በማዲሰን ቋሚ የቤተክርስቲያን ቤት ለማግኘት ማህበሩን ማዘጋጀት።",
      ],
      closingTitle: "ቃል ኪዳናችን",
      closingBody:
        "ይህ ገጽ ዛሬ ለማቅረብ በቂ ሲሆን በኋላ በካህናት ፈቃድ ቃላት፣ ታሪካዊ ማስታወሻዎች፣ ፎቶዎች እና የሀገረ ስብከት ማጣቀሻዎች ሊሻሻል ይችላል።",
    },
    events: {
      title: "አገልግሎቶችና ዝግጅቶች",
      intro: "የአምልኮ ጊዜዎችን፣ የማህበሩን መሰብሰቦች እና የመደበኛ የቤተክርስቲያን ሕይወት ስርዓትን የሚያቀርብ ግልጽ እይታ።",
      rhythmTitle: "መደበኛ የማህበሩ ስርዓት",
      featured: "ዋና",
      timeLabel: "ሰዓት",
      locationLabel: "ቦታ",
    },
    sermons: {
      title: "ስብከቶች",
      intro: "ለማህበሩ መልዕክቶች፣ አስተያየቶች እና የትምህርት ማጠቃለያዎች። ኦፊሴላዊ መዝገቦች ሲገኙ የድምጽ ወይም የቪዲዮ አገናኞች በኋላ ሊጨመሩ ይችላሉ።",
      archiveTitle: "የስብከት ማህደር አቅጣጫ",
      archiveBody:
        "ይህ የምርት ስሪት የማይሠሩ የመጫወቻ አዝራሮችን ሳይጠቀም ትርጉም ያለው የመጀመሪያ የስብከት ማጠቃለያ ያቀርባል።",
      by: "በ",
      scripture: "የመጽሐፍ ቅዱስ ክፍል",
      audioAvailable: "ድምጽ አለ",
      audioSoon: "ድምጽ በቅርቡ",
      videoAvailable: "ቪዲዮ አለ",
      videoSoon: "የቪዲዮ ማህደር በሂደት ላይ",
      sermons: [
        {
          title: "በእምነት መጽናት",
          speaker: "አቶ እሸቱ ዛቱ",
          dateLabel: "የቅርብ ጊዜ የማህበር ትምህርት",
          scripture: "ዕብራውያን 11",
          summary: "በቤተክርስቲያን ሕይወት ውስጥ ታማኝ፣ ጸሎታዊ እና ጽኑ እንድንሆን የሚያበረታታ መልዕክት።",
        },
        {
          title: "ጸሎት፣ ትዕግሥት እና ኦርቶዶክስ ምስክርነት",
          speaker: "የማህበሩ የትምህርት ቡድን",
          dateLabel: "የማህበር ማህደር",
          scripture: "ሮሜ 12",
          summary: "አምልኮ የዕለት ተዕለት ሕይወትን፣ የቤተሰብ ስልጠናን እና አገልግሎትን እንዴት እንደሚቀርጽ አስተያየት።",
        },
      ],
    },
    contact: {
      title: "ያግኙን",
      intro: "ለጥያቄዎች፣ ለጸሎት ጥያቄዎች፣ ለጉብኝት እቅድ ወይም ለማህበር ጉዳዮች ያነጋግሩን።",
      formTitle: "መልዕክት ይላኩ",
      formIntro: "ይህ የመገናኛ ቅጽ የማህበሩን ኢሜይል አድራሻ በቅድሚያ ተሞልቶ የእርስዎን ኢሜይል ፕሮግራም ይከፍታል።",
      name: "ስም",
      namePlaceholder: "ሙሉ ስምዎ",
      email: "ኢሜይል",
      emailPlaceholder: "you@example.com",
      phone: "ስልክ (አማራጭ)",
      phonePlaceholder: "(123) 456-7890",
      subject: "ርዕስ",
      subjectPlaceholder: "እንዴት ልንረዳዎት?",
      message: "መልዕክት",
      messagePlaceholder: "መልዕክትዎን እዚህ ይጻፉ",
      send: "መልዕክት ይላኩ",
      missingFields: "እባክዎ ስምዎን፣ ኢሜይልዎን እና መልዕክትዎን ይሙሉ።",
      emailClientOpened: "የኢሜይል ፕሮግራምዎ ለማህበሩ የተሞላ መልዕክት እንዲከፍት ይጠበቃል።",
      visitTitle: "ይጎብኙን ወይም ይደውሉ",
      planningTitle: "ጉብኝት እያቀዱ ነው?",
      planningBody: "እንግዶች እንኳን ደህና መጡ። ይህ የመጀመሪያ ጊዜዎ ከሆነ መልዕክት ይላኩ፣ ማህበሩም በአቅጣጫ፣ በጊዜ እና በሚጠበቁት ላይ ይረዳዎታል።",
      directions: "በGoogle Maps ላይ አቅጣጫ ይክፈቱ",
      mapTitle: "ወደ 11 N 7th Street, Madison, WI 53704 ካርታ",
    },
  },
};

const LanguageContext = createContext(null);

function getInitialLanguage() {
  if (typeof window === "undefined") {
    return "en";
  }

  const stored = window.localStorage.getItem(STORAGE_KEY);
  return stored === "am" ? "am" : "en";
}

function getValueByPath(source, path) {
  return path.split(".").reduce((accumulator, segment) => {
    if (accumulator && Object.prototype.hasOwnProperty.call(accumulator, segment)) {
      return accumulator[segment];
    }

    return undefined;
  }, source);
}

export function LanguageProvider({ children }) {
  const [language, setLanguage] = useState(getInitialLanguage);

  useEffect(() => {
    window.localStorage.setItem(STORAGE_KEY, language);
    document.documentElement.lang = language === "am" ? "am" : "en";
  }, [language]);

  const value = useMemo(() => {
    const dictionary = translations[language] || translations.en;

    return {
      language,
      setLanguage,
      t(path, fallback) {
        const localized = getValueByPath(dictionary, path);
        if (localized !== undefined) {
          return localized;
        }

        const english = getValueByPath(translations.en, path);
        return english !== undefined ? english : fallback;
      },
    };
  }, [language]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
  const context = useContext(LanguageContext);

  if (!context) {
    throw new Error("useLanguage must be used inside LanguageProvider");
  }

  return context;
}
